import React from 'react';
import { Card } from './ui/Card';
import { Code2, Gamepad2, Rocket } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { translations } from '../i18n/translations';
export function AboutSection() {
  const {
    language
  } = useLanguage();
  const t = translations[language].about;
  const stats = [{
    label: t.stats.projects,
    value: '4+',
    icon: <Rocket className="text-[#00f5a0]" data-id="element-3" />
  }, {
    label: t.stats.experience,
    value: '2+',
    icon: <Code2 className="text-[#1a4fd6]" data-id="element-4" />
  }, {
    label: t.stats.engines,
    value: '2',
    icon: <Gamepad2 className="text-[#00c9a7]" data-id="element-5" />
  }];
  return <section id="about" className="py-24 relative overflow-hidden" data-id="element-6">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-b from-[#1a4fd6]/5 to-transparent pointer-events-none" data-id="element-7"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-9">
          {/* Text Content */}
          <div className="space-y-8" data-id="element-10">
            <div data-id="element-11">
              <h2 className="text-3xl md:text-4xl font-bold mb-2" data-id="element-12">{t.title}</h2>
              <div className="h-1 w-20 bg-gradient-to-r from-[#1a4fd6] to-[#00f5a0] rounded-full" data-id="element-13"></div>
            </div>

            <div className="space-y-4 text-gray-300 text-lg leading-relaxed" data-id="element-14">
              <p data-id="element-15">{t.p1}</p>
              <p data-id="element-16">
                {t.p2}{' '}
                <span className="text-[#00c9a7] font-semibold" data-id="element-17">
                  {t.p2Highlight}
                </span>{' '}
                {t.p2End}
              </p>
              <p data-id="element-18">
                {t.p3}{' '}
                <span className="italic text-white" data-id="element-19">{t.p3Highlight}</span>
                {t.p3End}
              </p>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-20">
            {stats.map((stat, index) => index === 2 /* Game Engines — full card is the hover target */ ? <div key={index} className="sm:col-span-2 glass-card rounded-xl p-6 transition-all duration-500 hover:-translate-y-2 cursor-default group/engines" data-id="element-21">
                  <div className="flex items-center space-x-4" data-id="element-22">
                    <div className="p-3 bg-white/5 rounded-lg border border-white/10 flex-shrink-0" data-id="element-23">
                      {stat.icon}
                    </div>
                    <div className="relative min-h-[52px] flex items-center flex-1 overflow-hidden" data-id="element-24">
                      {/* Default: number + label */}
                      <div className="transition-all duration-300 group-hover/engines:opacity-0 group-hover/engines:scale-95" data-id="element-25">
                        <div className="text-3xl font-bold font-orbitron text-white" data-id="element-26">
                          {stat.value}
                        </div>
                        <div className="text-sm text-gray-400 uppercase tracking-wider" data-id="element-27">
                          {stat.label}
                        </div>
                      </div>
                      {/* Hover: engine logos */}
                      <div className="absolute inset-0 flex items-center gap-3 opacity-0 scale-95 transition-all duration-300 group-hover/engines:opacity-100 group-hover/engines:scale-100" data-id="element-28">
                        <div className="flex items-center gap-2 bg-black/40 border border-[#00f5a0]/30 rounded-lg px-2.5 py-1.5 flex-shrink-0 min-w-0" data-id="element-29">
                          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/unity/unity-original.svg" alt="Unity" className="w-5 h-5 brightness-0 invert flex-shrink-0" data-id="element-30" />
                          <span className="font-mono text-xs font-semibold text-[#00f5a0] tracking-wider" data-id="element-31">
                            Unity
                          </span>
                        </div>
                        <div className="flex items-center gap-2 bg-black/40 border border-[#00f5a0]/30 rounded-lg px-2.5 py-1.5 flex-shrink-0 min-w-0" data-id="element-32">
                          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/unrealengine/unrealengine-original.svg" alt="Unreal Engine 5" className="w-5 h-5 brightness-0 invert flex-shrink-0" data-id="element-33" />
                          <span className="font-mono text-xs font-semibold text-[#00f5a0] tracking-wider" data-id="element-34">
                            UE5
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> : <Card key={index} data-id="element-35">
                  <div className="flex items-center space-x-4" data-id="element-36">
                    <div className="p-3 bg-white/5 rounded-lg border border-white/10" data-id="element-37">
                      {stat.icon}
                    </div>
                    <div data-id="element-38">
                      <div className="text-3xl font-bold font-orbitron text-white" data-id="element-39">
                        {stat.value}
                      </div>
                      <div className="text-sm text-gray-400 uppercase tracking-wider" data-id="element-40">
                        {stat.label}
                      </div>
                    </div>
                  </div>
                </Card>)}

            {/* Decorative Quote Card */}
            <div className="sm:col-span-2 mt-4 p-6 rounded-xl border border-[#00f5a0]/20 bg-[#00f5a0]/5 backdrop-blur-sm" data-id="element-41">
              <p className="font-mono text-[#00f5a0] text-sm" data-id="element-42">
                {t.quote.line1}
                <br data-id="element-43" />
                {t.quote.line2}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}